import React from 'react';
import { useSelector } from 'react-redux';
import {
    selectActivityContentLocale,
    selectExpandedAccordionFromDetailState,
    selectGraphicContentFromGraphicState,
    selectIntroductionFromContentDetailGenericSectionState,
} from 'component/page/detail/store/detail.selectors';
import DetailInViewWrapper from 'component/common/DetailInViewWrapper/DetailInViewWrapper';
import { Accordion, Divider, Typography } from '@drone-js/core';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import { GenericContent } from 'gql/autogen/GenericContent';
import { Media } from 'gql/autogen/Media';
import { DetailContentOnToggleProps } from 'component/page/detail/DetailPageContent/DetailPageContent.selectors';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesIntroductionSummaryContainerSeenEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesIntroductionSummaryContainerClickEventActivitiesManifestV1,
    ActivitiesIntroductionSummaryContainerSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { setSelectedTabOnDetailsPage } from 'component/page/detail/store/details';
import { selectIsBotOrIsPrerenderFromBootstrap } from 'store/slice/bootstrap';
import { MediaType } from 'gql/autogen/MediaType';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';
import { InView } from 'react-intersection-observer';

const OverviewImageGraphicContent: React.FC<{ index: number; content: GenericContent; media: Media; contentLocale: string }> = ({ index, contentLocale, content, media }) => {
    return (
        <>
            {content.description && (
                <Typography data-testid="activities-graphic-title" variant="body-s" lang={contentLocale} textColor="base.subtle" tag="span" mt="16" mb="4">
                    {content.description}
                </Typography>
            )}
            {media?.source && <ActivityImage data-testid="activities-graphic-image" alt={media.description || `image${index}`} src={media.source} />}
        </>
    );
};
OverviewImageGraphicContent.displayName = 'OverviewImageGraphicContent';

const IntroductionSummary: React.FC<DetailContentOnToggleProps> = (props) => {
    const introductionSummary = useSelector(selectIntroductionFromContentDetailGenericSectionState);
    const graphicContent = useSelector(selectGraphicContentFromGraphicState);
    const contentLocale = useSelector(selectActivityContentLocale);
    const expandedAccordion = useSelector(selectExpandedAccordionFromDetailState);
    const isBotOrIsPrerender = useSelector(selectIsBotOrIsPrerenderFromBootstrap);
    const introductionSummarySectionState = expandedAccordion[`#${props.href}` || ''] || isBotOrIsPrerender ? 'expanded' : 'collapsed';
    const introductionSummaryEventIds = ActivitiesIntroductionSummaryContainerSeenEventActivitiesManifestV1.ids;

    const onSeen = (inView: boolean) => {
        if (inView && introductionSummary) {
            trackElementEvent<ActivitiesIntroductionSummaryContainerSeenEventActivitiesFieldsV1>(introductionSummaryEventIds, {
                is_toggle_on: introductionSummarySectionState === 'expanded',
            });
        }
    };

    if (!introductionSummary) {
        return null;
    }

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            <DetailInViewWrapper tabHref={props.href || ''} isScrolling={props.isScrolling} setSelectedTabCallback={setSelectedTabOnDetailsPage}>
                <Accordion
                    id={props.href}
                    actionType="chevron-icon"
                    contentSummary={introductionSummary?.title || ''}
                    backgroundColor="base.surface"
                    data-element-name="activities-introduction-summary-container"
                    data-testid="activities-introduction-summary-container"
                    level={2}
                    view={introductionSummarySectionState}
                    onToggle={props.onToggle(`#${props.href}` || '', ActivitiesIntroductionSummaryContainerClickEventActivitiesManifestV1.ids)}
                >
                    {introductionSummary?.content?.map((ic, index) => (
                        <Typography key={index} variant="body-s" lang={contentLocale} textColor="base.subtle" tag="span">
                            <AgodaMarkdown dataElementName="introduction-summary-description" input={ic?.description || ''} />
                        </Typography>
                    ))}
                    {graphicContent?.map((content, indexContent) => {
                        return content?.media
                            ?.filter((media) => media.mediaType === MediaType.IMAGE)
                            .map((media, indexMedia) => (
                                <OverviewImageGraphicContent
                                    key={`gc-${indexContent}-${indexMedia}`}
                                    index={indexMedia}
                                    content={content}
                                    contentLocale={contentLocale}
                                    media={media}
                                />
                            ));
                    })}
                </Accordion>
                <Divider direction="horizontal" my="4" />
            </DetailInViewWrapper>
        </InView>
    );
};
IntroductionSummary.displayName = 'IntroductionSummary';

export default IntroductionSummary;
